import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { FloatingFocusManager, FloatingPortal, size, useFloating, useFocus, useInteractions, useRole } from '@floating-ui/react';
import {
	Avatar,
	Badge,
	Grid,
	Icon,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	outlinedInputClasses,
	Paper,
	TextField,
	Typography,
	useTheme
} from '@mui/material';
import { Box } from '@mui/system';
import queryString from 'query-string';

import { KEYCODES, MODIFIERS } from 'Lib/constants/keyboard';
import ListTypes from 'Lib/constants/shared/listReportTypes';
import useKeyboardShortcut from 'Lib/hooks/useKeyboardShortcut';
import ShortcutIcon from 'Lib/muiComponents/common/ShortcutIcon';
import Dialog from 'Lib/muiComponents/dialog/Dialog';
import debounce from 'Lib/utilities/debounce';
import isEmpty from 'Lib/utilities/isEmpty';
import { removeDuplicates } from 'Lib/utilities/removeDuplicates';
import { screenSize } from 'Lib/utilities/screenSize';

import SavedSearch from './SavedSearch';

const Mobile = ({ children, fullscreen, open, toggle }) => (
	<>
		<IconButton aria-label="open saved searches" onClick={toggle}>
			search
		</IconButton>
		<Dialog open={open} toggle={toggle} fullscreen={fullscreen}>
			{children}
		</Dialog>
	</>
);

export const SearchInput = ({ placeholder, value, handleEnter, onFocus, onChange, ...rest }) => (
	<TextField
		data-cy="table-search-input"
		{...rest}
		required
		inputProps={{
			autoComplete: 'off',
			form: {
				autoComplete: 'off'
			}
		}}
		onFocus={onFocus}
		value={value}
		variant="outlined"
		autoComplete="off"
		aria-label={placeholder}
		placeholder={placeholder}
		sx={{
			m: 0,
			width: '100%',
			maxWidth: '100%',
			[`& .${outlinedInputClasses.root}`]: {
				height: '48px',
				pr: 0
			}
		}}
		onChange={event => onChange(event.target.value)}
		onKeyDown={handleEnter}
		InputProps={{
			startAdornment: (
				<InputAdornment position="start">
					<Icon>search</Icon>
				</InputAdornment>
			),
			endAdornment: value && (
				<IconButton
					aria-label="clear search"
					onClick={e => {
						e.stopPropagation();
						onChange('');
					}}>
					cancel
				</IconButton>
			)
		}}
	/>
);

/**
 *
 *
 */
export default function Search({ actions, api, fetch, listTypeID, placeholder = 'Search', search, files, ...rest }) {
	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();
	const { mobile, tablet } = screenSize();

	// Mobile open state
	const [open, setOpen] = useState(false);
	const toggleOpen = () => setOpen(prevState => !prevState);

	// FloatingUI open state
	const [isOpen, setIsOpen] = useState(false);

	const { refs, floatingStyles, context } = useFloating({
		placement: 'bottom-start',
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [
			size({
				apply({ rects, elements }) {
					Object.assign(elements.floating.style, {
						width: `${rects.reference.width}px`
					});
				}
			})
		]
	});

	const focus = useFocus(context);
	const role = useRole(context, {
		role: 'combobox'
	});

	// Shortcut to enter search field
	useKeyboardShortcut(MODIFIERS.CTRL, KEYCODES.S, () => {
		if (mobile || tablet) {
			return;
		}
		if (refs.domReference.current) {
			const input = refs.domReference.current.querySelector('input');
			if (input) input.focus();
		}
	});

	// TODO: Pull out Floating UI logic into a custom hook/component
	const { getReferenceProps, getFloatingProps } = useInteractions([focus, role]);

	const searches = useSelector(state => state.ui.searches)?.slice(0, 5);

	const [value, setValue] = useState('');
	const { search: searchInput } = queryString.parse(history.location.search);

	useEffect(() => {
		setValue(searchInput ?? '');
	}, [searchInput]);

	const handleEnter = e => {
		// keyCode 13 represents the Enter/Return button for mobile purposes
		if (e.keyCode === 13) {
			handleSearch(value);
		}
	};

	const setIsOpenFalse = () => {
		setIsOpen(false);
	};

	const handleSearch = useCallback(
		debounce(value => {
			dispatch(actions.setSearch(value));
			const updatedSearchParameters = {
				...queryString.parse(history.location.search),
				search: value
			};

			history.replace({
				search: queryString.stringify(updatedSearchParameters)
			});

			if (search) search(updatedSearchParameters);
		}, 1000),
		[]
	);

	const onChange = value => {
		setValue(value);
		handleSearch(value);
		setIsOpen(false);
	};

	const handleNavigate = search => {
		onChange(search);
	};

	const RecentSearchesTitle = () => <Typography sx={{ fontSize: theme.typography.pxToRem(20), letterSpacing: '0.15px' }}>Recent Searches</Typography>;

	const EmptySearches = ({ mobile }) => (
		<Box sx={{ ...(!mobile && { p: 4 }), ...(mobile && { pt: 4 }) }}>
			<RecentSearchesTitle />
			<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, width: '100%' }}>
				<Avatar sx={{ bgcolor: 'success.secondary', height: 96, width: 96 }}>
					<Badge overlap="circular" badgeContent={<Icon sx={{ color: 'success.main' }}>auto_awesome</Icon>}>
						<Icon sx={{ color: 'success.main', fontSize: 56 }}>search</Icon>
					</Badge>
				</Avatar>
			</Box>
			<br />
			<Typography sx={{ textAlign: 'center' }}>No recent searches.</Typography>
		</Box>
	);

	const RecentSearches = ({ mobile }) => (
		<Box sx={{ ...(mobile && { pt: 4 }) }}>
			{mobile && <RecentSearchesTitle />}
			<List>
				{removeDuplicates(searches).map((search, index) => (
					<ListItem key={`${search}_${index}`} disablePadding>
						<ListItemButton onClick={() => handleNavigate(search)}>
							<ListItemText>{search}</ListItemText>
						</ListItemButton>
					</ListItem>
				))}
			</List>
		</Box>
	);

	return mobile ? (
		<>
			<IconButton aria-label="open saved searches" onClick={toggleOpen}>
				search
			</IconButton>
			<Dialog open={open} toggle={toggleOpen} fullscreen={tablet}>
				<Typography
					sx={{
						fontSize: theme => theme.typography.pxToRem(20),
						letterSpacing: '0.15px',
						mb: 4
					}}>
					{`${ListTypes.getName(listTypeID) ? ListTypes.getName(listTypeID) + ' ' : ''}Search`}
				</Typography>
				<SearchInput {...rest} placeholder={placeholder} value={value} onChange={setValue} handleEnter={handleEnter} />
				{isEmpty(searches) ? <EmptySearches mobile /> : <RecentSearches mobile />}
			</Dialog>
		</>
	) : (
		<>
			<TextField
				ref={refs.setReference}
				{...getReferenceProps({ onFocus: () => setIsOpen(true) })}
				fullWidth
				data-cy="table-search-input"
				{...rest}
				required
				value={value}
				variant="outlined"
				autoComplete="none"
				inputProps={{ autoComplete: 'none' }}
				placeholder={placeholder}
				aria-label={placeholder}
				sx={{
					m: 0,
					[`& .${outlinedInputClasses.root}`]: {
						height: '48px',
						pr: 0
					}
				}}
				onChange={event => onChange(event.target.value)}
				onKeyDown={handleEnter}
				InputProps={{
					autoComplete: 'none',
					startAdornment: (
						<InputAdornment position="start">
							<Icon>search</Icon>
						</InputAdornment>
					),
					endAdornment: value ? (
						<IconButton
							aria-label="clear search"
							onClick={e => {
								e.stopPropagation();
								setValue('');
								handleSearch();
								// set focus on this elements inner input
								refs.domReference.current.querySelector('input').focus();
							}}>
							cancel
						</IconButton>
					) : (
						// check to see if current element is focused
						<ShortcutIcon inputs={['Ctrl', 'S']} />
					)
				}}
			/>
			<FloatingPortal>
				{isOpen && (
					// Modal, guards and returnFocus are disabled to allow the user to click outside of the floating UI & input to close it/blur the input
					<FloatingFocusManager context={context} modal={false} returnFocus={false} order={['reference', 'content']}>
						<Box ref={refs.setFloating} sx={{ position: 'relative', zIndex: 3 }} style={floatingStyles} {...getFloatingProps()}>
							<Paper sx={{ overflow: 'hidden' }} elevation={8}>
								{/* TODO: [RENTVINE-8265] Hide saved (filter-based) searches when view doesn't use them.*/}
								<Grid container>
									<Grid item xs={6}>
										{isEmpty(searches) ? <EmptySearches /> : <RecentSearches />}
									</Grid>
									{!files && (
										<Grid item xs={6} sx={{ bgcolor: 'common.offwhite' }}>
											<SavedSearch inline actions={actions} api={api} fetch={fetch} handleClose={setIsOpenFalse} />
										</Grid>
									)}
								</Grid>
							</Paper>
						</Box>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</>
	);
}
